import React from "react";
import styled from "@emotion/styled";
import PlaceholderCardIconGraphic from "@ecologi/assets/icons/ProjectInsights/empty-card-icon.svg";
import { Typography } from "../Typography";
import Image from "next/legacy/image";
import { ProjectTypeCard, ProjectTypeCardProps } from "./ProjectTypeCard";
import CertificationIconGraphic from "@ecologi/assets/icons/ProjectInsights/certification-icon.svg";
import { Link as LinkBase } from "../Link";
import {
  ProjectCertification,
  ProjectCertificationMapper,
  ProjectSize,
} from "@ecologi/shared";
import { getResizedImageURL } from "../utils/get-resized-image-url";

const CardWrapper = styled.div`
  height: 606px;
  width: 356px;

  background-color: ${(props) => props.theme.colors.snowdrop};

  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  width: 100%;
  height: 100%;
  padding: 24px;

  display: flex;
  flex-direction: column;

  background-color: ${(props) => props.theme.colors.mist};
`;

const Title = styled(Typography)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  &:hover {
    color: ${(props) => props.theme.colors.forest} !important;
    text-decoration: underline;
    cursor: pointer;
  }
`;

const Link = styled(LinkBase)`
  text-decoration: none !important;
`;

const Description = styled(Typography)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: block-axis;
  margin-bottom: 16px;
  margin-top: 12px;
  overflow: hidden;
`;

const CardFooter = styled.div`
  background-color: ${(props) =>
    props.theme.colors.opacity(props.theme.colors.mist, 0.5)};
  min-height: 88px;
  padding: 0 24px;

  display: flex;
  justify-content: center;
  flex-direction: column;
  width: 100%;
`;

const CertificationWrapper = styled.div`
  display: flex;
  text-align: center;
  align-items: center;

  padding-bottom: 8px;

  &:last-child {
    padding-bottom: 0px;
  }
`;

const CertificationIcon = styled(CertificationIconGraphic)`
  margin-right: 5px;
`;

const ImageWrapper = styled(LinkBase)`
  background-color: ${(props) => props.theme.colors.mist};
`;
export type ProjectInsightsCardProps = {
  imageUrl: string;
  title: string;
  description: string;
  certifications: ProjectCertification[];
  size: ProjectSize;
  projectLink: string;
} & ProjectTypeCardProps;

export const ProjectInsightsCard = ({
  imageUrl,
  title,
  description,
  variant,
  certifications,
  carbonTotal,
  projectLink,
}: ProjectInsightsCardProps) => {
  const resizedImageUrl = getResizedImageURL(imageUrl, 712, 400, {
    crop: true,
  });

  return (
    <CardWrapper>
      {resizedImageUrl && (
        <ImageWrapper href={projectLink} target="_blank" rel="noopener">
          <Image
            src={resizedImageUrl}
            width={356}
            height={200}
            loading="lazy"
          />
        </ImageWrapper>
      )}
      <ContentWrapper>
        <Link href={projectLink} target="_blank" rel="noopener">
          <Title use="componentTitle" as="h2">
            {title}
          </Title>
        </Link>
        <Description use="body1">{description}</Description>
        <ProjectTypeCard variant={variant} carbonTotal={carbonTotal} />
      </ContentWrapper>
      <CardFooter>
        {certifications
          .map((certification) => (
            <CertificationWrapper key={certification}>
              <CertificationIcon />
              <Typography use="dashboardMicroCopyRegular" as="p">
                {ProjectCertificationMapper[certification].title}
              </Typography>
            </CertificationWrapper>
          ))
          .slice(0, 2)}
      </CardFooter>
    </CardWrapper>
  );
};

const PlaceholderInner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  max-width: 60%;
  text-align: center;
`;

const PlaceholderCardIcon = styled(PlaceholderCardIconGraphic)`
  padding-bottom: 20px;
  height: 100%;
`;

const Placeholder = () => {
  return (
    <CardWrapper>
      <PlaceholderInner>
        <PlaceholderCardIcon />
        <Typography use="body1" as="p">
          Try a different filter to see more projects.
        </Typography>
      </PlaceholderInner>
    </CardWrapper>
  );
};

ProjectInsightsCard.Placeholder = Placeholder;
